import { useCommunityDetail } from '@communities/community/hooks';
import { SectionHeader, ContentRatingDisplay } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Card, Shimmer, Text, Button, ButtonType, OverlayMode, ExternalLink, Heading, ChevronDown, ChevronUp } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './CommunityDetailsSection.css';

interface CommunityDetailsSectionProps {
  onEditCommunity?: () => void;
}

interface DescriptionFieldProps {
  description: string;
}

/**
 * DescriptionField Component
 *
 * Displays the community description with "Show more" / "Show less" functionality.
 * Truncates to 5 lines by default with a toggle to expand/collapse.
 */
function DescriptionField({ description }: DescriptionFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={styles.fieldContainer}>
      <Heading level="h2" size="sm">
        Description
      </Heading>
      <Text className={isExpanded ? undefined : styles.truncatedDescription} color="primary" lineHeight="relaxed" size="base">
        {description}
      </Text>
      <button className={styles.showMoreButton} type="button" onClick={() => setIsExpanded(!isExpanded)}>
        <span className={styles.showMoreText}>{isExpanded ? 'Show less' : 'Show more'}</span>
        {isExpanded ? <ChevronUp className={styles.chevronIcon} /> : <ChevronDown className={styles.chevronIcon} />}
      </button>
    </div>
  );
}

/**
 * CommunityDetailsSection Component
 *
 * Displays core community information including description, content rating, and genres.
 * Uses the ContentRatingDisplay component which handles all the formatting internally.
 * Includes an Edit button for admins to modify community details.
 */
function CommunityDetailsSectionComponent({ onEditCommunity }: CommunityDetailsSectionProps) {
  const { community, loading } = useCommunityDetail();

  const { description, contentGenres, pgRating, contentRating, contentStandardsUrl } = community?.optionalMetadata || {};

  // Loading state with shimmer
  if (loading && !community) {
    return (
      <div className={styles.shimmerContainer}>
        <Shimmer className={styles.shimmerTitle} />
        <Shimmer className={styles.shimmerSubtitle} />
        <Shimmer className={styles.shimmerContent} />
      </div>
    );
  }

  return (
    <>
      <div className={styles.headerContainer}>
        <SectionHeader
          description="Content rating, genre, community standards and description affect how classification is performed."
          title="Community Details"
        />
        {onEditCommunity && (
          <Button buttonType={ButtonType.Secondary} overlayMode={OverlayMode.Solid} onClick={onEditCommunity}>
            EDIT
          </Button>
        )}
      </div>

      <Card padding="none">
        <div className={styles.cardContent}>
          <div className={styles.twoColumnLayout}>
            {/* Left Column - Content & Genre */}
            <div className={styles.leftColumn}>
              <div className={styles.fieldContainer}>
                <Heading level="h2" size="sm">
                  Content & Genre
                </Heading>
                <ContentRatingDisplay contentRating={contentRating} genres={contentGenres} pgRating={pgRating} />
              </div>
            </div>

            {/* Right Column - Content Standards and Description */}
            <div className={styles.rightColumn}>
              {/* Content Standards Row */}
              {contentStandardsUrl && (
                <div className={styles.fieldContainer}>
                  <Heading level="h2" size="sm">
                    Content Standards
                  </Heading>
                  <ExternalLink href={contentStandardsUrl} variant="standard">
                    View Content Standards
                  </ExternalLink>
                </div>
              )}

              {/* Description Row */}
              {description && <DescriptionField description={description} />}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}

export const CommunityDetailsSection = withAppInsightsTracking(CommunityDetailsSectionComponent, 'CommunityDetailsSection');
